import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { UserDataModel } from '../_models/user-data-model';
import { PermissionConstants } from '../shared/permission-constants';
import { EngravingService } from './engraving.service';
import { HttpService } from './http.service';
import { LocalstorageService } from './localstorage.service';

@Injectable({
	providedIn: 'root'
}) export class UserService implements OnDestroy {

	private readonly userSessionKey = "userdata";
	private readonly adminModeSessionKey = "adminmodeenabled";
	private subscriptions = new Array<Subscription>();
	public currentUser$: BehaviorSubject<UserDataModel | null>;
	private adminModeEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public get currentUser() {
		return this.currentUser$.asObservable();
	}

	public get adminModeEnabled() {
		return this.adminModeEnabled$.asObservable();
	}

	public userCanAddChanges: Subject<void> = new Subject<void>();
	public engravingSubscription: Subscription = new Subscription();

	constructor(
		private localStorageService: LocalstorageService,
		private engravingService: EngravingService,
		private httpService: HttpService
	) {
		const user = this.localStorageService.get<UserDataModel | null>(this.userSessionKey);

		this.currentUser$ = new BehaviorSubject<UserDataModel | null>(user ? plainToClass(UserDataModel, user) : null);

		this.initAdminMode(user);

		this.subscriptions.push(this.engravingSubscription = this.engravingService.engravingChanged.subscribe(() => {
			this.userCanAddChanges.next();
		}));
	}

	private initAdminMode(user: UserDataModel | null) {
		
		this.adminModeEnabled$.subscribe((enabled) => {
			this.localStorageService.set(this.adminModeSessionKey, enabled);
		});

		const hasAdminPermission = user?.UserPermissions?.some(per => per == PermissionConstants.IsAdmin) ?? false;
		if (hasAdminPermission) {
			const adminModeEnabled = this.localStorageService.get<boolean>(this.adminModeSessionKey) ?? true;

			this.adminModeEnabled$.next(adminModeEnabled);
		}
		else {
			this.adminModeEnabled$.next(false);
		}
	}

	ngOnDestroy(): void {
		this.engravingSubscription.unsubscribe();
	}

	public setUser(user: UserDataModel | null) {
		const currentUser = this.currentUser$.value;

		if (currentUser) {
			if (currentUser.LoginRegistered && user) {
				user.LoginRegistered = currentUser.LoginRegistered;
			}

			if (currentUser.IsLiteLogin && user) {
				user.IsLiteLogin = currentUser.IsLiteLogin;
			}
		}

		this.localStorageService.set(this.userSessionKey, user);
		this.currentUser$.next(user);
		this.validateAdminMode();
	}

	public removeUserDataFromLocalStorage() {
		this.localStorageService.clear(this.userSessionKey);
	}

	public async GetUser(): Promise<UserDataModel | null> {
		return new Promise<UserDataModel | null>((resolve, reject) => {
			resolve(this.currentUser$.value);
		});
	}

	async UserCanOnlyPlaceTestorder(customerChosen: boolean): Promise<boolean> {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return user.canOnlyTestOrder(customerChosen);
	}

	async UserHasAddToBagPermission(): Promise<boolean> {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return new Promise<boolean>((resolve) => {
			if (user.UserPermissions?.some(y => y == PermissionConstants.CanAddToBag)) {
				resolve(true);
			}

			resolve(false);
		});
	};

	UserHasCanAddToBagPermission(): boolean | undefined {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return user.UserPermissions?.some(y => y == PermissionConstants.CanAddToBag);
	};

	async UserCanAddToBag(): Promise<boolean> {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		const userHasAddToBagPermission = await this.UserHasAddToBagPermission();
		const engravngError = this.engravingService.engravingError();
		return userHasAddToBagPermission && engravngError.length == 0;
	}

	async HasProductlineStatus(productLineCodes: Array<string>): Promise<boolean> {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return user.hasAllProductlines(productLineCodes);
	}

	NewCustomerModuleEnabled(): boolean | undefined {
		const user = this.currentUser$.value;

		if (user === null && user !== undefined) {
			return false;
		}

		return user.newCustomerModuleEnabled;
	}

	PreciousModuleEnabled(): boolean | undefined {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return user.preciousModuleEnabled;
	}

	public PriceModuleEnabled(): boolean | undefined {
		const user = this.currentUser$.value;

		if (user === null) {
			return false;
		}

		return user.priceModuleEnabled;
	}

	private validateAdminMode() {
		const adminModeEnabled = this.localStorageService.get<boolean>(this.adminModeSessionKey);

		if (adminModeEnabled == null) {
			const newValue = this.currentUser$.value != null && this.currentUser$.value.isAdmin && (adminModeEnabled === true || adminModeEnabled == null);
			this.adminModeEnabled$.next(newValue);
		}
	}

	public adminModeToggle() {
		const adminModeEnabled = this.localStorageService.get<boolean>(this.adminModeSessionKey) ?? true;
		this.adminModeEnabled$.next(!adminModeEnabled);
	}

	public removeAdminModeEnabledEntry() {
		this.localStorageService.clear(this.adminModeSessionKey);
		this.localStorageService.clear(this.adminModeSessionKey.toLocaleLowerCase());
	}

	public async getUserData(): Promise<UserDataModel> {
		let url = `${environment.dataUrl}/Userdata`;
		const result = await this.httpService.getDataWithOfflineCheck<UserDataModel>(url);
		return plainToClass(UserDataModel, result);
	}
}
